import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';

const BRAND_LOGO = "/brand/byard-logo.svg";
const BRAND_ACRONYM = "/brand/byard-logo-acronym.jpg";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axiosInstance.post('/auth/reset-password', {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4 py-12" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-4">
            <img 
              src={BRAND_LOGO} 
              alt="BYARD Logo" 
              className="h-24 w-auto object-contain"
              data-testid="brand-logo"
            />
          </div>
          <img 
            src={BRAND_ACRONYM} 
            alt="BYARD" 
            className="h-10 mx-auto object-contain"
            data-testid="brand-acronym"
          />
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
          {success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ph-bold ph-check-circle text-3xl text-green-600"></i>
              </div>
              <h2 data-testid="success-title" className="text-xl font-bold text-[#2B3A67] mb-2">
                Password Reset!
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                Your password has been successfully reset. You can now login with your new password.
              </p>
              <button
                data-testid="go-to-login-button"
                onClick={() => navigate('/auth')}
                className="w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold shadow-lg"
              >
                Go to Login
              </button>
            </div>
          ) : error && !token ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ph-bold ph-warning text-3xl text-red-600"></i>
              </div>
              <h2 data-testid="error-title" className="text-xl font-bold text-[#2B3A67] mb-2">
                Invalid Link
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                {error}
              </p>
              <button
                data-testid="request-new-link-button"
                onClick={() => navigate('/forgot-password')}
                className="w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold shadow-lg"
              >
                Request New Link
              </button>
            </div>
          ) : (
            <>
              <h2 data-testid="reset-password-title" className="text-xl font-bold text-[#2B3A67] text-center mb-2">
                Create New Password
              </h2>
              <p className="text-gray-500 text-sm text-center mb-6">
                Enter your new password below. Make sure it's at least 6 characters.
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                  <p className="text-red-600 text-sm text-center">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-gray-400">
                    New Password
                  </label>
                  <input
                    id="password"
                    data-testid="new-password-input"
                    type="password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562]"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="text-xs font-bold uppercase tracking-widest text-gray-400">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    data-testid="confirm-password-input"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562]"
                  />
                </div>

                <button
                  data-testid="reset-password-button"
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold shadow-lg disabled:opacity-50"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 text-center">
            <button
              data-testid="back-to-login-link"
              onClick={() => navigate('/auth')}
              className="text-[#2B3A67] font-semibold text-sm hover:underline flex items-center justify-center gap-2 mx-auto"
            >
              <i className="ph-bold ph-arrow-left"></i>
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
